import type { TTeacherDataBooking } from "@/components/modals/class-request/TeacherModal.vue";
import type { RouteNamedMap } from "vue-router/auto-routes";

export type TObjEntries<T> = {
	[K in keyof T]: [K, T[K]];
}[keyof T][];

export type TExtendedBool = 0 | 1 | boolean;

export type TRole = {
	rol_id: number;
	rol_name: string;
	rol_label: string;
};

export type TFileData = {
	fld_id: string;
	download_link: string;
	fld_contenttype: string;
	fld_name: string;
	fld_size: number;
	fil_id: number;
};
export type TFile = {
	fil_id: number;
	fil_name: string;
	fil_deleted: TDate;
	usr_id: number;
	fil_private: TExtendedBool;
	ftp_code: TFileTypeValues;
	ftp_name: string;
	fil_created: TDate;
	data: TFileData[];
};

export type TPermissionGroup = {
	pgr_id: number;
	trn_id: number;
	pgr_name: number;
};

export type TCountry = {
	cnt_code: string;
	cnt_name: string;
};

export type TDate = string | Date;
export type TUserStatus = "Active" | "Inactive" | "Banned";

export type TUser = {
	usr_id: number;
	rol_id: TRole["rol_id"];
	fil_id_avatar: TFile["fil_id"];
	lng_code_email: string;
	usr_fullname: string;
	usr_password: string;
	usr_receivenotifications: TExtendedBool;
	usr_receiveemails: TExtendedBool;
	cnt_code: TCountry["cnt_code"];
	usr_email: string;
	usr_status: TUserStatus;
	usr_hourlyrate: string;
	usr_description: string;
	usr_remotework: TExtendedBool;
	usr_phone: string;
	usr_schoolrole: string;
	usr_availabilityupdated: TDate;
	usr_accountapproved: TDate;
	usr_verified: TDate;
	usr_blacklisted: TDate;
	usr_created: TDate;
	usr_updated: TDate;
	usr_deleted: TDate;
};

export type TBaseUser = Pick<
	TUser,
	| "usr_id"
	| "usr_fullname"
	| "usr_email"
	| "usr_status"
	| "cnt_code"
	| "usr_phone"
	| "fil_id_avatar"
	| "rol_id"
>;

export type TUserAuth = {
	usr_email: TUser["usr_email"];
	usr_password: TUser["usr_password"];
	token?: string;
};

export type TTeacher = TBaseUser &
	Partial<
		Pick<
			TUser,
			| "usr_description"
			| "usr_remotework"
			| "usr_updated"
			| "usr_deleted"
			| "usr_blacklisted"
			| "usr_accountapproved"
			| "usr_availabilityupdated"
		>
	>;

export const TAG_TYPES = {
	PROGRAMS: "PGRM",
	SCHOOL_TYPE: "SCTP",
	SKILLS: "SKLS",
} as const;

export type TTagType = keyof typeof TAG_TYPES;

export type TTag = {
	tag_id: number;
	tag_name: string;
	tag_type?: (typeof TAG_TYPES)[TTagType];
	tag_created?: string;
	tag_description?: string;
	trn_id?: number;
	tgt_code?: (typeof TAG_TYPES)[TTagType];
};

export type TTagView = Omit<TTag, "tag_type">;

export type TFieldOfStudy = {
	sfp_id: number;
	sfp_id_parent?: number;
	sfp_name: string;
	sfp_created: TDate;
	sfp_updated: TDate;
	sfp_deleted: TDate;
	translations: TTranslation<Pick<TFieldOfStudy, "sfp_name">>;
};

export type TFieldOfStudyView = Omit<
	TFieldOfStudy,
	"sfp_created" | "sfp_updated" | "sfp_deleted" | "trn_id"
> & { selectable?: boolean; childs?: TFieldOfStudyView[] };

export type TFieldOfStudyViewExpandable = Omit<TFieldOfStudyView, "childs"> & {
	childs: TFieldOfStudyViewExpandable[];
	expanded: boolean;
	edit: boolean;
};

export type TItemExpandable<T> = {
	[k in keyof Omit<T, "expanded" | "edit" | "childs">]: T[k];
} & {
	expanded: boolean;
	edit: boolean;
	childs: T;
};

export type TSubscriptionStatus = "Active" | "Canceled" | "Expired";

export type TSubscription = {
	sbr_id: number;
	scl_id: TSchool["scl_id"];
	sbr_price: number;
	sbr_start: TDate;
	sbr_end: TDate;
	sbr_status: TSubscriptionStatus;
	sbr_created: TDate;
	sbr_updated: TDate;
	sbr_deleted: TDate;
};

export type TRegion = {
	rgn_id: number;
	cnt_code: TCountry["cnt_code"];
	rgn_name: string;
};

export type TCity = {
	cnt_code: TCountry["cnt_code"];
	cit_zipcode: string;
	cit_id: number;
	rng_id: TRegion["rgn_id"];
	cit_name: string;
};

export type TSchoolLocation = {
	slc_id: number;
	slc_name: string;
	slc_phone: string;
	slc_updated: TDate;
	scl_id: TSchool["scl_id"];
	slc_address: string;
	slc_email: string;
	slc_deleted: TDate;
	cit_id: TCity["cit_id"];
	cit_name: TCity["cit_name"];
	slc_contactname: string;
	slc_created: TDate;
};

export type TSchool = {
	scl_id: number;
	cnt_code: TCountry["cnt_code"];
	scl_hourlyrate: number;
	scl_updated: TDate;
	slc_id_main: TSchoolLocation["slc_id"];
	scl_name: string;
	scl_websiteurl: string;
	scl_deleted: TDate;
	fil_id_logo: TFile["fil_id"];
	scl_description: string;
	scl_created: TDate;
	cit_name?: string;
	scl_logo?: string | null;
	administrators: TSchoolAdmin[];
	locations?: TSchoolLocation[];
	nature: any[];
	subscription?: TSchoolSubscription;
	scl_address?: string;
	scl_email?: string;
	scl_phone?: string;
	contract_types?: TContractType[];
	subjects: TFieldOfStudyInfo[];
	scl_accreditations?: string[];
	scl_contactname?: string;
	cit_id: TCity["cit_id"];
	programs?: TTag[];
};

export type TSchoolSubscription = {
	sbr_id: number;
	scl_id: number;
	sbr_price: number;
	sbr_status: string;
	sbr_start: string;
	sbr_end: string;
	sbr_created: string;
};

export type TSchoolAdminInfo = Pick<
	TUser,
	"usr_email" | "usr_fullname" | "usr_schoolrole" | "usr_id"
>;

export type TSchoolSuperAdminView = Pick<
	TSchool,
	| "scl_id"
	| "cnt_code"
	| "scl_hourlyrate"
	| "scl_created"
	| "scl_name"
	| "scl_address"
	| "scl_email"
	| "scl_phone"
	| "scl_contactname"
	| "scl_updated"
> & {
	locations: TSchoolLocation[];
	nature: TTag[];
	subscription: TSubscription;
	administrators: TSchoolAdminInfo[];
};

export type TRouteName = keyof RouteNamedMap;

export type TPagination = {
	total: number;
	count: number;
	per_page: number;
	current_page: number;
	offset: number;
	limit: number;
	total_pages: number;
};

export type TMetaInfo = {
	pagination: TPagination;
	[k: string]: any;
};

export type TCurrency = {
	cc: string;
	symbol: string;
	name: string;
};

export type TLocale = "en" | "fr";

export type TCreateLocation = { slc_id?: number } & Pick<
	TSchoolLocation,
	| "slc_name"
	| "slc_address"
	| "slc_contactname"
	| "slc_email"
	| "slc_phone"
	| "cit_id"
>;

export type TCreateSchool = Pick<
	TSchool,
	| "scl_name"
	| "scl_address"
	| "scl_email"
	| "scl_phone"
	| "scl_contactname"
	| "cit_id"
> & {
	locations: TCreateLocation[];
	natures: { tag_id: TTag["tag_id"] & Partial<Omit<TTag, "tag_id">> }[];
};

export type TTranlsationLocales = "fr";

export type TTranslation<T extends Record<string, unknown>> = {
	[a in TTranlsationLocales]?: T;
};
export type TFilterObject = {
	label: string;
	value: string | number;
};

export type TTeacherClassRequest = {
	crq_id: number;
	crq_status: "OPEN" | "ACCEPTED" | "REJECTED" | "CANCELED";
	crq_title: string;
	crq_note: string;
	crq_expense: boolean;
	from: string; // Format: "YYYY-MM-DD HH:MM:SS"
	to: string; // Format: "YYYY-MM-DD HH:MM:SS"
	total_count: number;
	total_duration: string; // Example: "39min"
	scl_id: number;
	scl_name: string;
	classes: TTeacherClass[];
	response_time?: string;
	files?: TFile[];
};

export type TLanguage = {
	lng_code: string;
	lng_name: string;
};

export type TFieldOfStudyInfo = {
	sfp_id: number;
	sfp_name: string;
};
export type TFieldOfStudySelectable = {
	sfp_id: number;
	sfp_name: string;
	selectable: boolean;
	children;
};

export type TFieldOfStudyApi = {
	sfp_id: number;
	sfp_name: string;
	sfp_id_parent?: number;
	childs: TFieldOfStudyApi[];
	selectable: boolean;
};

export type TTeacherProfile = {
	usr_id: number;
	usr_fullname: string;
	usr_email: string;
	usr_status: string;
	usr_hourlyrate?: number;
	usr_experience: number;
	usr_professionalexperience: number;
	usr_title: string;
	usr_availabilityupdated: TDate;
	usr_phone: string;
	usr_avatar?: string;
	subjects: TFieldOfStudyInfo[];
	skills: TTag[];
	languages: TLanguage[];
	usr_description: string;
	cities: TCity[];
	contract_types: TContractType[];
	usr_remotework?: 0 | 1;
	experiences: {
		EDUC: TEducationItem[];
		PEXP: TProfessionalExperienceItem[];
		TEXP: TTeachingExperienceItem[];
	};
	usr_profilecompleted?: TDate;
	usr_accountapproved?: TDate;
	files: TFile[];
	average_response_time?: string;
};

export type TContractType = {
	ctt_code: string;
	ctt_name: string;
};

export type TCvCode = "EDUC" | "PEXP" | "TEXP";
export type TCourseLevel =
	| "MIDDLE"
	| "HIGH"
	| "BACHELOR"
	| "MASTER"
	| "ABOVE_MASTER"
	| null;

export type TCvExperience = {
	cve_id: number;
	cvt_code: TCvCode;
	usr_id_teacher: number;
	cve_title: string;
	cve_company: string;
	cve_start: string;
	cve_end?: string;
	cve_description: string;
	cve_hourlyvolume: number;
	cve_coursetitle: string;
	cve_courselevel: TCourseLevel;
	// cve_coursecontent: string;
	ongoing?: boolean;
};
export type TEducationItem = Pick<
	TCvExperience,
	"cve_title" | "cve_company" | "cve_start" | "cve_end" | "ongoing"
> & { cve_id?: TCvExperience["cve_id"]; cvt_code: "EDUC" };

export type TTeachingExperienceItem = Omit<
	TCvExperience,
	"cve_id" | "cvt_code" | "usr_id_teacher"
> & {
	cve_id?: TCvExperience["cve_id"];
	cvt_code: "TEXP";
};

export type TPartialCvExperienceWithId = {
	cve_id: TCvExperience["cve_id"];
} & Partial<Omit<TCvExperience, "cve_id">>;

export type TProfessionalExperienceItem = Pick<
	TCvExperience,
	| "cve_title"
	| "cve_company"
	| "cve_start"
	| "cve_end"
	| "cve_description"
	| "ongoing"
> & {
	cve_id?: TCvExperience["cve_id"];
	cvt_code: "PEXP";
};

export type TFileTypeValues =
	| "Addr_Rec"
	| "Bank_Acc"
	| "Cart_Vit"
	| "Comp_Reg"
	| "Crim_Rec"
	| "Diploma_"
	| "Extr_Doc"
	| "ID_Pic_C"
	| "Image___"
	| "NHIR_Crt"
	| "Prof_Lia"
	| "Resume__"
	| "Tax_Prof";

export type TFileType = {
	ftp_code: TFileTypeValues;
	ftp_name: string;
	drq_optional?: boolean;
};
export type TTeacherClass = {
	cls_id: number;
	cls_start: string;
	cls_end: string;
	cls_info: string;
	cls_title: string;
	cls_status: "OPEN" | "ACCEPTED" | "REJECTED" | "CANCELED";
	crr_id: number;
	scl_name: string;
	slc_address: string;
	slc_id: number;
	cit_id: number;
	cit_name: string;
	clr_reason: string | null;
	cls_created: string;
	cls_deleted: string | null;
	usr_admin: number;
	usr_email: string | null;
	usr_id_teacher: number | null;
};

export type TFeedbackType = "approved" | "disapproved" | "pending";

export interface IEduObj {
	cve_title: IBaseTeachObj;
	cve_company: IBaseTeachObj;
	cve_start: IBaseTeachObj;
	cve_end: IBaseTeachObj;
	cve_description: IBaseTeachObj;
	cve_hourlyvolume: IBaseTeachObj;
	cve_coursetitle: IBaseTeachObj;
	// cve_coursecontent: IBaseTeachObj;
	cve_courselevel: IBaseTeachObj;
}

export interface IProfExpObj {
	cve_title: IBaseTeachObj;
	cve_company: IBaseTeachObj;
	cve_start: IBaseTeachObj;
	cve_end: IBaseTeachObj;
	cve_description: IBaseTeachObj;
}

export interface ITeachingExpObj {
	cve_title: IBaseTeachObj;
	cve_company: IBaseTeachObj;
	cve_start: IBaseTeachObj;
	cve_end: IBaseTeachObj;
	cve_description: IBaseTeachObj;
	cve_hourlyvolume: IBaseTeachObj;
	cve_coursetitle: IBaseTeachObj;
	// cve_coursecontent: IBaseTeachObj;
	cve_courselevel: IBaseTeachObj;
}

export interface ITeacherInfoObj {
	usr_ttl: IBaseTeachObj;
	usr_flnm: IBaseTeachObj;
	usr_eml: IBaseTeachObj;
	usr_hrrt: IBaseTeachObj;
	usr_avtr: IBaseTeachObj;
}

export type TTeacherInfo = {
	wasValidated: boolean;
	full_name: string;
	user: ITeacherInfoObj;
	experiences: {
		edu: ITeachingExpObj[];
		pexp: IProfExpObj[];
		texp: ITeachingExpObj[];
	};
	files: IFile[];
};

export interface IBaseTeachObj {
	value: string;
	id: number;
}

export type TFeedbackPayload = {
	usr_id: number;
	vrq: TInfoFeedback[];
	cve: TExpFeedback[];
	fil: TFileFeedback[];
};

interface IFeedback<T extends string, U> {
	[key: string]: U | string;
	reason: string;
}

export type TInfoFeedback = IFeedback<"vrq_id", number>;
export type TExpFeedback = IFeedback<"cve_id", number>;
export type TFileFeedback = IFeedback<"fil_id", number>;

export interface IFile {
	id: number;
	title: string;
	links: IFileLinks[];
}
interface IFileLinks {
	name: string;
	url: string;
}

export type TValidatePayload = {
	usr_id: number;
	vrq_ids: number[];
	cve_ids: number[];
	fil_ids: number[];
};

export interface IPosition {
	top: number;
	left: number;
}

export interface IPopupEvent {
	id?: number;
	title: string;
	date: string;
	schoolName: string;
	courseName?: string;
	address: string;
	info: string;
	isEditable?: boolean;
	isCompleted?: boolean;
	isNoShow?: boolean;
	isDeletable?: boolean;
	additionalData?: Record<string, any>;
}

export interface ICalendarEvent {
	id: number;
	title: string;
	subtitle?: string;
	time?: string;
	date: string;
	color?: string;
	isEditable?: boolean;
	isDeletable?: boolean;
}
export type TTeacherData = {
	usr_availabilityupdated: string | null;
	usr_avatar: string;
	usr_created: string;
	usr_email: string;
	usr_experience: number;
	usr_fullname: string;
	usr_hourlyrate: number;
	usr_id: number;
	usr_professionalexperience: number;
	usr_profilecompleted: string | null;
	usr_status: string;
	usr_title: string;
	languages?: TLanguage[];
	cities?: TCity[];
	skills?: TTag[];
	average_response_time?: string;
};

export type TTeacherSearchFilters = {
	usr_fullname?: string;
	usr_status?: string;
	usr_hourlyrate?: number;
	usr_experience?: number;
	usr_professionalexperience?: number;
	hired?: boolean;
	contract_types?: string; // temp
	usr_remotework?: boolean;
	rate_to?: number;
	rate_from?: number;
	skills?: TTag[];
	cities?: TCity[];
	languages?: TLanguage[];
	subjects?: TFieldOfStudyInfo[];
};

export type TTeacherSearchFiltersBooking = {
	usr_fullname?: string;
	usr_status?: string;
	usr_hourlyrate?: number;
	usr_experience?: number;
	usr_professionalexperience?: number;
	contract_types?: string; // temp
	usr_remotework?: boolean;
	rate_to?: number;
	rate_from?: number;
	skills?: TTag[];
	cities?: TCity[];
	languages?: TLanguage[];
};

export type TSchoolAdmin = {
	usr_id: number;
	usr_fullname: string;
	usr_email: string;
	usr_status: string;
	usr_verified: string | null;
	usr_avatar: string | null;
	usr_created: string;
	usr_profilecompleted: string | null;
	usr_lastlogin: string | null;
	usr_schoolrole: string;
};

export type TCourseInformation = {
	date: Date | null;
	end_date?: Date | null;
	start_time?: Date | null;
	end_time?: Date | null;
	name: string;
	campus: TSchoolLocation | null;
	program: TTag | null;
	note?: string | null;
	is_single_class?: boolean;
	teacher?: TTeacherDataBooking | null;
	subjects?: TFieldOfStudyInfo[];
	files: TFile;
};

export type TClassInfoPattern = {
	selectedDays: string[];
	startTime: Date | null;
	endTime: Date | null;
	campus: TSchoolLocation | null;
	note: string | null;
};
export type TNotification = {
	ntf_id: number;
	ntf_created: string;
	ntf_deleted: string | null;
	ntf_json: string;
	ntf_ref_id: number | null;
	ntf_ref_id_2: number | null;
	ntf_seen: string | null;
	ntf_text: string;
	ntf_title: string;
	ntp_code: string;
	usr_id_created: number;
	usr_id_recipient: number;
};

export type TPendingExperience = {
	cve_id: number;
	columns: (
		| "cve_title"
		| "cve_company"
		| "cve_start"
		| "cve_end"
		| "cve_description"
		| "cve_hourlyvolume"
		// | "cve_coursecontent"
		| "cve_coursetitle"
		| "cve_courselevel"
	)[];
};

export type TPendingData = {
	columns: (
		| "usr_fullname"
		| "usr_email"
		| "usr_title"
		| "usr_avatar"
		| "usr_hourlyrate"
	)[];
	files: number[];
	experiences: TPendingExperience[];
};

export type TTeacherCalendarFiltersType = {
	schools?: { scl_id: number; scl_name: string }[];
	course_titles: string[];
};

export type TSchoolCalendarFiltersType = {
	programs?: {
		tag_id: number;
		tag_name: string;
	}[];
	course_titles?: string[];
	teachers: {
		usr_id: number;
		usr_fullname: string;
	}[];
	statuses: string[];
};

export type TWsData = {
	type: string;
	notification: TNotification;
};

export type TClassesFilters = {
	cities: { cit_id: number; cit_name: string }[];
	schools: { scl_id: number; scl_name: string }[];
	from: string | null;
	to: string | null;
};

export type TClassManagerFilters = {
	programs: { tag_id: number; tag_name: string }[];
	teachers: { usr_id: number; usr_name: string }[];
	from: string | null;
	to: string | null;
};

export type TTeacherCalendarSelectedFiltersType = {
	schools?: {
		scl_id: number;
		scl_name: string;
		checked: boolean;
		color?: string;
	}[];
	course_titles: { crs_name: string; checked: boolean }[];
};

export type TSchoolCalendarSelectedFiltersType = {
	programs?: {
		tag_id: number;
		tag_name: string;
		checked: boolean;
	}[];
	course_titles?: { crs_name: string; checked: boolean }[];
	teachers: { usr_id: number; usr_fullname: string; checked: boolean }[];
};

export type TCalendarColorItem = {
	color: string;
	label: string;
};

export type TCalendarClass = {
	cit_id: number | null;
	cit_name: string | null;
	clr_reason: string | null;
	cls_created: string;
	cls_deleted: string | null;
	cls_end: string;
	cls_id: number;
	cls_info: string;
	cls_start: string;
	cls_status: "OPEN" | "ACCEPTED" | "REJECTED" | "CANCELED";
	cls_title: string;
	crq_id: number;
	crq_note: string | null;
	crq_status: "OPEN" | "ACCEPTED" | "REJECTED" | "CANCELED";
	crq_title: string;
	crr_id: number;
	scl_id: number | null;
	scl_name: string | null;
	slc_address: string | null;
	slc_id: number | null;
	slc_name: string | null;
	usr_admin: number;
	usr_email: string;
	usr_fullname: string;
	usr_id_teacher: number;
};
